import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Download as DownloadIcon,
  Mui,
  MuiIcons,
} from "@leap4/frontend";
import React, { useContext, useState } from "react";
import { ReportContext } from "../reportContext";
import { SavedTemplate } from "../templates/types";
import { extractErrorMessage } from "../../../../utils/functions";
import ReportBuilderView from "./reportBuilderView";
import ReportingGuide from "./reportingGuide";
import SavedTemplates from "./savedTemplates";

const { Menu, MenuItem, ListItemText, Paper } = Mui;
const { ArrowDropDown } = MuiIcons;

interface ReportTabsProps {
  saveReportTemplate: (
    name: string,
    description: string,
    asCopy: boolean
  ) => Promise<boolean>;
  deleteReport: (report: SavedTemplate) => Promise<void>;
  runReportTemplate: (report: SavedTemplate) => Promise<void>;
  clearReport: () => void;
  updateReport: (report: SavedTemplate) => Promise<void>;
  overwriteTemplate?: (template: SavedTemplate) => Promise<void>;
  copyTemplate?: (template: SavedTemplate) => Promise<void>;
  templatesLoading: boolean;
  templatesError: string | null;
  templateBusy: boolean;
  refreshTemplates: () => Promise<void>;
}

const ReportTabs: React.FC<ReportTabsProps> = (props) => {
  const {
    tabIndex,
    setTabIndex,
    report,
    reportTemplates,
    reportData,
    metadata,
    exportAllAsCsv,
    exportLeadIds,
    isExporting,
    isExportingLeadIds,
    isFetchingReportData,
  } = useContext(ReportContext);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState(false);
  const [asCopy, setAsCopy] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const busy =
    props.templateBusy ||
    isFetchingReportData ||
    isExporting ||
    isExportingLeadIds;
  const openSave = (copy: boolean) => {
    setAsCopy(copy);
    setName(copy && report._id ? `${report.name} (copy)` : report.name);
    setDescription(report.description);
    setError(null);
    setDialog(true);
  };
  const save = async () => {
    if (!name.trim()) {
      setError("Enter a template name.");
      return;
    }
    if (report.idCampaign.includes(",")) {
      setError("Select one campaign, or all of them, to save a template.");
      return;
    }
    const clash = reportTemplates.find((t) => t.name.trim().toLowerCase() === name.trim().toLowerCase() && t.id !== (asCopy ? undefined : report._id));
    if (clash) {
      setError(asCopy && report._id === clash.id ? `"${clash.name}" is the template you have open. Use Update "${clash.name}" to overwrite it, or pick a new name.` : `A template named "${clash.name}" already exists. Pick a different name, or open it from Saved templates and update it.`);
      return;
    }
    setError(null);
    try {
      if (await props.saveReportTemplate(name, description, asCopy))
        setDialog(false);
    } catch (e) {
      setError(
        extractErrorMessage(e) ||
          (e instanceof Error
            ? e.message
            : "Could not save this template. Please try again.")
      );
    }
  };
  const hasResults = !!reportData && reportData.length > 0;
  const hasDates = !!report.startDate && !!report.endDate;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Typography variant="h4" component="h1">
          Reporting
        </Typography>
        {tabIndex === 0 && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button color="inherit" disabled={busy} onClick={props.clearReport}>
              Clear
            </Button>
            {report._id && (
              <Button variant="outlined" disabled={busy || !metadata.loaded} onClick={() => openSave(false)}>
                Update "{report.name}"
              </Button>
            )}
            <Button variant="outlined" disabled={busy || !metadata.loaded} onClick={() => openSave(true)}>
              Save as template
            </Button>
            <Button
              variant="contained"
              disabled={busy || (!hasResults && !hasDates)}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(exportAnchor) || undefined}
              startIcon={isExporting || isExportingLeadIds ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              endIcon={<ArrowDropDown />}
            >
              {isExporting || isExportingLeadIds ? "Exporting…" : "Export"}
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} slotProps={{ paper: { sx: { mt: 1, maxWidth: 320 } } }}>
              <MenuItem disabled={!hasResults} onClick={() => { setExportAnchor(null); exportAllAsCsv(); }}>
                <ListItemText primary="CSV" secondary={hasResults ? "Every row, not just this page" : "Run the report first"} slotProps={{ secondary: { sx: { whiteSpace: "normal" } } }} />
              </MenuItem>
              <MenuItem disabled={!hasDates} onClick={() => { setExportAnchor(null); exportLeadIds(); }}>
                <ListItemText primary="Lead IDs" secondary="For this campaign and date range only." slotProps={{ secondary: { sx: { whiteSpace: "normal" } } }} />
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
      <Tabs value={tabIndex} onChange={(_event, value) => setTabIndex(value)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
        <Tab label="Report builder" />
        <Tab label="Saved templates" />
        <Tab label="Reporting guide" />
      </Tabs>
      {tabIndex === 0 && (
        <>
          {report._id && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} role="status">
              Template "{report.name}" is loaded. Changes are saved only when you choose Update "{report.name}".
            </Typography>
          )}
          <ReportBuilderView />
        </>
      )}
      {tabIndex === 1 && (
        <SavedTemplates
          deleteReport={props.deleteReport}
          openTemplate={props.runReportTemplate}
          overwriteTemplate={props.overwriteTemplate}
          copyTemplate={props.copyTemplate}
          createTemplate={() => {
            props.clearReport();
            setTabIndex(0);
          }}
          loading={props.templatesLoading}
          error={props.templatesError}
          busy={props.templateBusy}
          refresh={props.refreshTemplates}
        />
      )}
      {tabIndex === 2 && (
        <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", p: { xs: 2, md: 3 } }}>
          <ReportingGuide />
        </Paper>
      )}
      <Dialog
        open={dialog}
        onClose={() => {
          if (!props.templateBusy) setDialog(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {asCopy ? "Save as template" : `Update "${report.name}"`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Shared with everyone in the selected organization. Saves campaign,
            dates, dimensions, metrics, builder filters and metric constraints.
            Results, table sorting and quick search are not saved.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Template name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={props.templateBusy}
            margin="dense"
            slotProps={{
              htmlInput: { maxLength: 100 }
            }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={props.templateBusy}
            margin="dense"
            slotProps={{
              htmlInput: { maxLength: 1000 }
            }}
          />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {report.preset !== "choose time preset"
              ? `Time preset: ${report.preset.replace(
                  "last calender",
                  "Last calendar"
                )} — recalculated when opened or run.`
              : `Dates: ${report.startDate || "Choose a start date"} → ${
                  report.endDate || "Choose an end date"
                }`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {report.timezone} · {report.dimensions.length} dimensions ·{" "}
            {report.metrics.length} metrics
          </Typography>
          {error && (
            <Typography role="alert" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={props.templateBusy}
            onClick={() => setDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={props.templateBusy || !name.trim()}
            onClick={save}
          >
            {props.templateBusy
              ? "Saving…"
              : asCopy
              ? "Save template"
              : "Update template"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ReportTabs;
